import { cnTwMerge } from "@vega-core/utils/cnTwMerge";

import { RenderAction } from "./components/RenderActions";
import { RenderFilters } from "./components/RenderFilters";
import { ResetFiltersButton } from "./components/ResetFiltersButton";
import { SaveFilters } from "./components/SaveFilters/SaveFilters";
import { SaveFiltersResults } from "./components/SaveFilters/SaveFiltersResults";
import {
  type FilterState,
  type FilterValue,
  type TableFilterId,
  type TableFiltersConfig,
} from "./types";

type TableFiltersProps = {
  config: TableFiltersConfig;
  filterState: FilterState;
  onFilterChange: (filterId: string, value: FilterValue) => void;
  onResetFilters: () => void;
  hasActiveFilters: boolean;
  activeFilterCount: number;
  isLoading?: boolean;
  showSavedFilters?: boolean;
  id: TableFilterId;
  applyContextInPersistenceKeys?: boolean;
};

export const TableFilters = ({
  config,
  filterState,
  onFilterChange,
  onResetFilters,
  hasActiveFilters,
  activeFilterCount,
  isLoading = false,
  showSavedFilters = false,
  id,
  applyContextInPersistenceKeys = true,
}: TableFiltersProps) => {
  const shouldShowResetButton =
    config.showResetButton !== false && hasActiveFilters && !showSavedFilters;

  return (
    <div
      className={cnTwMerge("flex flex-col gap-4 mb-4 w-full", {
        "mb-0": config.noMarginBottom,
      })}
    >
      <div className="w-full flex gap-3 items-center justify-between">
        <div className="flex-1 flex gap-3 w-full">
          <RenderFilters
            config={config}
            disabled={config.disabled}
            filterState={filterState}
            isLoading={isLoading}
            onFilterChange={onFilterChange}
            onResetFilters={onResetFilters}
            tableId={id}
          />
          {shouldShowResetButton ? (
            <ResetFiltersButton
              activeFilterCount={activeFilterCount}
              disabled={config.disabled}
              onResetFilters={onResetFilters}
            />
          ) : null}
          {showSavedFilters ? (
            <SaveFilters
              applyContextInPersistenceKeys={applyContextInPersistenceKeys}
              disabled={config.disabled}
              filterState={filterState}
              hasActiveFilters={hasActiveFilters}
              id={id}
              resetFilters={onResetFilters}
            />
          ) : null}
        </div>

        {config.actions ? (
          <div className="flex items-center gap-4">
            {config.actions.map((action) => (
              <RenderAction
                action={action}
                isLoading={isLoading}
                key={action.id}
                tableId={id}
              />
            ))}
          </div>
        ) : null}
      </div>

      {showSavedFilters ? (
        <SaveFiltersResults
          applyContextInPersistenceKeys={applyContextInPersistenceKeys}
          disabled={config.disabled}
          filterState={filterState}
          id={id}
          onFilterChange={onFilterChange}
        />
      ) : null}
    </div>
  );
};
